import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormTimePicker from "../../components/formFields/FormTimeField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import {
  emailWOValueValidator,
  MobileNoValidator,
  referalCodeValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import { clearBookingDetails } from "./bookingSlice";
import { getAllRooms } from "../room/services/room.services";
import {
  createBooking,
  getBookingByID,
  updateBooking,
  ValidateReferalCode,
} from "./services/booking.services";
import { MdCheck } from "react-icons/md";
import { RxCrossCircled } from "react-icons/rx";
import { Loader } from "@progress/kendo-react-indicators";
import IconComponent from "../../components/common/IconComponent";
import { ErrorToast } from "../../components/toast/Toasts";
import { findIndex } from "lodash";
import { ICity } from "../city/cityModel";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { IState } from "../state/stateModel";
import { getAllActiveLeadSource } from "../leadsource/services/leadsource.service";
import { ILeadSource } from "../leadsource/leadsourceModel";

interface CityChangeWatcherProps {
  formRenderProps: FormRenderProps;
  CityList: ICity[];
}

const CityChangeWatcher: React.FC<CityChangeWatcherProps> = ({
  CityList,
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const cityIndex = findIndex(CityList, { ID: CityID });
    if (cityIndex > -1) {
      const city = CityList[cityIndex];
      formRenderProps.onChange("StateID", { value: city.StateID });
    }
  }, [cityID]);
  return null;
};

const CreateBooking: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<any>(null);
  const [referalCode, setReferalCode] = useState<null | boolean>(null);

  const BookingID = location.state?.BookingID;
  const BookingDetail = useAppSelector((state) => state.booking.BookingDetail);
  const formLoading = useAppSelector((state) => state.booking.formLoading);
  const ValidateRefCode = useAppSelector(
    (state) => state.booking.ValidateRefCode,
  );

  const RoomList = useAppSelector((state) => state.room.RoomList);
  const CityList = useAppSelector((state) => state.city.CityList);
  const StateList = useAppSelector((state) => state.state.StateList);
  const LeadSourceList = useAppSelector(
    (state) => state.leadsource.LeadSourceList,
  );

  useEffect(() => {
    if (!RoomList || RoomList.length === 0) {
      dispatch(getAllRooms());
    }
    if (!CityList || CityList.length === 0) {
      dispatch(getAllActiveCities());
    }
    if (!StateList || StateList.length === 0) {
      dispatch(getAllActiveStates());
    }
    if (!LeadSourceList || LeadSourceList.length === 0) {
      dispatch(getAllActiveLeadSource());
    }
  }, [dispatch, RoomList, CityList, StateList, LeadSourceList]);

  const roomOptions = (RoomList || []).map((room: any) => ({
    label: room.RoomTitle,
    value: room.RoomTitle,
  }));

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (BookingID) {
      dispatch(getBookingByID(BookingID));
    }
    return () => {
      dispatch(clearBookingDetails());
    };
  }, [BookingID, dispatch]);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [BookingDetail]);

  const handleSubmit = async (values: any) => {
    const code = values.ReferalCode;
    if (code) {
      if (referalCode === false) {
        ErrorToast("Please Enter Valid Referal Code");
        return;
      }
      if (referalCode === null && !BookingID) {
        if (code.length !== 6) {
          setReferalCode(false);
          ErrorToast("Referal code must be 6 digit");
          return;
        }
        const res = await dispatch(ValidateReferalCode(code));
        if (res.meta.requestStatus === "fulfilled") {
          setReferalCode(true);
        } else {
          setReferalCode(false);
          ErrorToast("Please Enter Valid Referal Code");
          return;
        }
      }
    }
    try {
      const formattedBookingDate = values.BookingDate
        ? moment(values.BookingDate).format("YYYY-MM-DD")
        : "";
      const formattedCheckinDate = values.CheckinDate
        ? moment(values.CheckinDate).format("YYYY-MM-DD")
        : "";
      const formattedCheckinTime = values.CheckinTime
        ? moment(values.CheckinTime).format("HH:mm A")
        : "";

      const payload = {
        ...(BookingID ? { ID: BookingID } : {}),
        BookingDate: formattedBookingDate,
        CheckinDate: formattedCheckinDate,
        CheckinTime: formattedCheckinTime,
        ReferalCode: values.ReferalCode || "",
        Name: values.Name || "",
        MobileNo: values.MobileNo || "",
        EmailID: values.EmailID || "",
        Address: values.Address || "",
        CityID: values.CityID ? +values.CityID : null,
        StateID: values.StateID ? +values.StateID : null,
        NoOfRooms: Number(values.NoOfRooms || 0),
        NoOfPax: Number(values.NoOfPax || 0),
        RoomType: values.RoomType || "",
        SourceID: values.SourceID ? +values.SourceID : null,
        Remarks: values.Remarks || "",
      };

      const response = BookingID
        ? await dispatch(updateBooking(payload))
        : await dispatch(createBooking(payload));

      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(clearBookingDetails());
        navigate("/booking");
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleValidateReferalCode = async (ReferalCode: string) => {
    if (!ReferalCode) return;
    if (ReferalCode.length === 6) {
      const res = await dispatch(ValidateReferalCode(ReferalCode));
      if (res.meta.requestStatus === "fulfilled") setReferalCode(true);
      else setReferalCode(false);
    } else {
      setReferalCode(false);
      ErrorToast("Referal code must be 6 digit");
    }
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={BookingDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <CityChangeWatcher
                formRenderProps={formRenderProps}
                CityList={CityList}
              />
              <ShadowCard style={{ padding: 16 }}>
                <GridLayout
                  style={{ marginRight: 60 }}
                  gap={{ rows: 0, cols: 20 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography.h4 style={{ margin: 0 }}>
                        {BookingID ? "Update Booking" : "Add Booking"}
                      </Typography.h4>
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Name"
                      label="Customer Name"
                      placeholder="e.g. John Doe"
                      maxLength={100}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="MobileNo"
                      label="Mobile No"
                      placeholder="e.g. 9876543210"
                      maxLength={15}
                      type="text"
                      component={FormTextField}
                      validator={MobileNoValidator}
                      astrike={true}
                      onKeyPress={(e: any) => {
                        const allowedChars = /^[0-9+]$/;
                        if (!allowedChars.test(e?.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="EmailID"
                      label="Email Address"
                      placeholder="e.g. john@example.com"
                      component={FormTextField}
                      validator={emailWOValueValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="ReferalCode"
                      label="Referal Code"
                      placeholder="Enter Reffered By"
                      maxLength={6}
                      wrapperStyle={{ width: "100%" }}
                      disabled={BookingID}
                      component={FormTextField}
                      validator={referalCodeValidator}
                      valid={referalCode === false ? false : undefined}
                      customSuffix={
                        ValidateRefCode ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              backgroundColor: "#e8f0fe",
                            }}
                          >
                            <Loader size="small" themeColor="primary" />
                          </div>
                        ) : referalCode === true ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              backgroundColor: "#d1e7dd",
                            }}
                          >
                            <IconComponent
                              icon={MdCheck}
                              style={{
                                fontSize: "18px",
                                color: "#0f5132",
                              }}
                            />
                          </div>
                        ) : referalCode === false ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              backgroundColor: "#f8d7da",
                            }}
                          >
                            <IconComponent
                              icon={RxCrossCircled}
                              style={{
                                fontSize: "18px",
                                color: "#842029",
                              }}
                            />
                          </div>
                        ) : null
                      }
                      onChange={() => {
                        setReferalCode(null);
                      }}
                      onBlurCapture={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (e?.target?.value)
                          referalCode === null &&
                            handleValidateReferalCode(e.target.value);
                        else setReferalCode(null);
                      }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="RoomType"
                      label="Room Type"
                      placeholder="Select Room Type"
                      component={FormSelectionField}
                      options={roomOptions}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="BookingDate"
                      label="Booking Date"
                      component={FormDatePicker}
                      validator={requiredValidator}
                      onChange={(e: any) => {
                        const newBookingDate = e.value;
                        const checkinDate =
                          formRenderProps.valueGetter("CheckinDate");
                        if (
                          newBookingDate &&
                          checkinDate &&
                          moment(newBookingDate).isAfter(
                            moment(checkinDate),
                            "day",
                          )
                        ) {
                          formRenderProps.onChange("CheckinDate", {
                            value: null,
                          });
                        }
                      }}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="CheckinDate"
                      label="Check-in Date"
                      component={FormDatePicker}
                      validator={requiredValidator}
                      astrike={true}
                      minDate={
                        formRenderProps.valueGetter("BookingDate")
                          ? formRenderProps.valueGetter("BookingDate")
                          : moment().toDate()
                      }
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="CheckinTime"
                      label="Check-in Time"
                      component={FormTimePicker}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="NoOfRooms"
                      label="No. of Rooms"
                      min={0}
                      format="n0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="NoOfPax"
                      label="No. of Pax (Guests)"
                      min={0}
                      format="n0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      id={"CityID"}
                      name={"CityID"}
                      label={"City"}
                      placeholder="Select City"
                      component={FormSelectionField}
                      isAddNew={true}
                      isIndirect={true}
                      drawerName="cityForm"
                      options={CityList?.map((city: ICity) => {
                        return {
                          value: city?.ID,
                          label: city?.CityName,
                        };
                      })}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      id={"StateID"}
                      name={"StateID"}
                      label={"State"}
                      placeholder="Select State"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={StateList?.map((state: IState) => {
                        return {
                          value: state?.ID,
                          label: state?.StateName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"SourceID"}
                      name={"SourceID"}
                      label={"Source"}
                      placeholder="Select Source"
                      component={FormSelectionField}
                      isAddNew={true}
                      isIndirect={true}
                      drawerName="leadSourceForm"
                      options={LeadSourceList?.map((leadSource: ILeadSource) => {
                        return {
                          value: leadSource?.ID,
                          label: leadSource?.Source,
                        };
                      })}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Address"
                      label="Address"
                      placeholder="Full address"
                      component={FormTextArea}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="Remarks"
                      label="Remarks"
                      placeholder="Additional notes or requests"
                      component={FormTextArea}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      textAlign: "end",
                      marginTop: "20px",
                    }}
                  >
                    <ButtonWithLoading
                      label={BookingID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || formLoading}
                      loading={formLoading}
                    />

                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/booking")}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateBooking;
